import { SyslogLevel, LogModule, LogComponent, syslogLevelToString, isHighPriorityLevel } from './loggerTypes'
import type { LoggerClientConfig, LogEntry } from './loggerTypes'
import { readCommonInfo } from './sessionContextReader'
import { emitOtelLog, forceFlushOtel } from './otelLogEmitter'
import { context, trace } from '@opentelemetry/api'

// ── Sanitization helpers (ported from logManager.ts) ──────────────────────────

const REDACTED_KEYS = [
  'password', 'token', 'authorization', 'auth',
  'secret', 'cookie', 'set-cookie', 'email', 'session',
]

function sanitizeKey(key: string): boolean {
  const lower = key.toLowerCase()
  return REDACTED_KEYS.some(k => lower.includes(k))
}

function truncateText(value: string, max = 4000): string {
  if (value.length <= max) return value
  return `${value.slice(0, max)}... [TRUNCATED ${value.length - max} chars]`
}

type FlatMeta = Record<string, string>

function flattenObject(value: unknown, output: FlatMeta, prefix: string, depth: number): void {
  if (depth > 3 || value == null) return
  if (Object.keys(output).length >= 100) return
  if (Array.isArray(value)) { output[prefix] = `[array:${value.length}]`; return }
  if (typeof value !== 'object') {
    output[prefix] = truncateText(String(value), 1024)
    return
  }
  for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
    const next = prefix ? `${prefix}.${k}` : k
    if (sanitizeKey(k)) { output[next] = '[REDACTED]'; continue }
    flattenObject(v, output, next, depth + 1)
  }
}

function sanitizeMeta(meta: unknown): FlatMeta {
  if (meta === undefined) return {}
  const out: FlatMeta = {}
  flattenObject(meta, out, 'data', 0)
  return out
}

// ── Console routing ────────────────────────────────────────────────────────────

function consoleFnForLevel(level: SyslogLevel): (...args: unknown[]) => void {
  if (level <= SyslogLevel.error) return console.error
  if (level === SyslogLevel.warning) return console.warn
  if (level <= SyslogLevel.info) return console.info
  return console.debug
}

function resolveDisplayName(value: number | string, lookup: Record<string, unknown>): string {
  if (typeof value === 'string') return value
  const entry = Object.entries(lookup).find(([, v]) => v === value)
  return entry ? entry[0] : String(value)
}

// ── Logger class ──────────────────────────────────────────────────────────────

/**
 * Core Logger class. Writes structured log entries to console and optionally
 * to an OpenTelemetry pipeline (OTLP → Collector → App Insights).
 *
 * Accepts either a plain LoggerClientConfig (snapshot) or a getter function
 * () => LoggerClientConfig (lazy — used by AppLogger singleton so all loggers
 * immediately reflect config updates from ConfigContext).
 */
export class Logger {
  private configGetter: () => LoggerClientConfig
  private boundContext: Record<string, unknown>

  constructor(
    configOrGetter: LoggerClientConfig | (() => LoggerClientConfig),
    boundContext: Record<string, unknown> = {}
  ) {
    this.configGetter = typeof configOrGetter === 'function'
      ? configOrGetter
      : () => configOrGetter
    this.boundContext = boundContext
  }

  private get config(): LoggerClientConfig {
    return this.configGetter()
  }

  /** Returns a new Logger that overrides the component label. */
  public cloneWithComponent(component: string | number): Logger {
    const getter = this.configGetter
    return new Logger(() => ({ ...getter(), component }), this.boundContext)
  }

  /** Returns a new Logger with extra data merged into commonInfo on every call. */
  public withContext(data: Record<string, unknown>): Logger {
    return new Logger(this.configGetter, { ...this.boundContext, ...data })
  }

  public emerg(message: string, data?: unknown) { this.log(SyslogLevel.emerg, message, data) }
  public alert(message: string, data?: unknown) { this.log(SyslogLevel.alert, message, data) }
  public crit(message: string, data?: unknown) { this.log(SyslogLevel.crit, message, data) }
  public error(message: string, data?: unknown) { this.log(SyslogLevel.error, message, data) }
  public warning(message: string, data?: unknown) { this.log(SyslogLevel.warning, message, data) }
  public warn(message: string, data?: unknown) { this.log(SyslogLevel.warning, message, data) }
  public notice(message: string, data?: unknown) { this.log(SyslogLevel.notice, message, data) }
  public info(message: string, data?: unknown) { this.log(SyslogLevel.info, message, data) }
  public debug(message: string, data?: unknown) { this.log(SyslogLevel.debug, message, data) }

  private log(level: SyslogLevel, message: string, data?: unknown): void {
    const config = this.config
    if (level > config.logLevel) return

    const baseCommonInfo = readCommonInfo(config.commonInfoKeys)
    const commonInfo: Record<string, unknown> = { ...baseCommonInfo, ...this.boundContext }

    try {
      const activeSpan = trace.getSpan(context.active())
      const spanCtx = activeSpan?.spanContext()
      if (spanCtx?.traceId && spanCtx?.spanId) {
        commonInfo.traceId = spanCtx.traceId
        commonInfo.spanId = spanCtx.spanId
      }
    } catch { /* never let trace correlation fail the app */ }

    const entry: LogEntry = {
      logLevel: level,
      ts: Math.floor(Date.now() / 1000),
      module: config.module,
      component: config.component,
      commonInfo,
      message,
      data,
    }

    this.emitToConsole(entry)

    if (config.otel?.enabled) {
      emitOtelLog(entry)
      if (isHighPriorityLevel(level)) {
        forceFlushOtel()
      }
    }
  }

  private emitToConsole(entry: LogEntry): void {
    const consoleFn = consoleFnForLevel(entry.logLevel)

    if (this.config.consoleStructured) {
      consoleFn(entry)
      return
    }

    const iso = new Date(entry.ts * 1000).toISOString()
    const levelStr = syslogLevelToString(entry.logLevel).toUpperCase()
    const moduleStr = resolveDisplayName(
      entry.module as number | string,
      LogModule as unknown as Record<string, unknown>
    )
    const componentStr = resolveDisplayName(
      entry.component as number | string,
      LogComponent as unknown as Record<string, unknown>
    )
    const prefix = `[${iso}] ${levelStr} [${moduleStr}:${componentStr}]`
    const formatted = truncateText(entry.message)

    const extras: Record<string, unknown> = {}
    if (Object.keys(entry.commonInfo).length > 0) extras.commonInfo = entry.commonInfo
    if (entry.data !== undefined) extras.data = sanitizeMeta(entry.data)

    if (Object.keys(extras).length > 0) {
      consoleFn(`${prefix} ${formatted}`, extras)
    } else {
      consoleFn(`${prefix} ${formatted}`)
    }
  }
}
